/**
 * Static Pricing Data
 * ============================================
 * Single source of truth for all pricing information.
 * Used by frontend-public (SEO pages), frontend-app, and backend.
 */
import { PlatformFeatureCode, PlatformLimitType, PlatformPlanSlug, PlatformPlanTier, } from "./PlatformPlan.js";
// ============================================
// PLAN DEFINITIONS - Single Source of Truth
// ============================================
export const STATIC_PLATFORM_PLANS = [
    {
        slug: PlatformPlanSlug.STARTER,
        name: "Starter",
        tier: PlatformPlanTier.STARTER,
        description: "Get started with basic features",
        pricing: {
            USD: { monthlyCents: 1900, annualMonthlyCents: 1500 }, // $19/mo or $15/mo annually (20% off)
            BRL: { monthlyCents: 9900, annualMonthlyCents: 7900 }, // R$99/mo or R$79/mo annually
        },
        // Small "try-it" AI budget (USD). ~$0.25 (≈25k tokens @ blended seed rate).
        aiBudget: { centsPerMonth: 25 },
        trialDays: 30,
        isFree: false,
        isPopular: false,
        displayFeatures: ["oneEstablishment", "oneUser", "upTo50Animals"],
        entitlements: [
            {
                featureCode: PlatformFeatureCode.MAX_ESTABLISHMENTS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 1,
            },
            {
                featureCode: PlatformFeatureCode.MAX_STAFF_MEMBERS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 1,
            },
            {
                featureCode: PlatformFeatureCode.MAX_ANIMALS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 50,
            },
            {
                // Starter AI-on (P3 §6): the small `aiBudget` above (USD 25 / BRL
                // 130) is what now governs Starter — enforcement reads the USD cost
                // budget, not this boolean's old `false`.
                featureCode: PlatformFeatureCode.LLM_ACCESS,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.ANALYTICS_DASHBOARD,
                enabled: false,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.PRIORITY_SUPPORT,
                enabled: false,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
        ],
    },
    {
        slug: PlatformPlanSlug.PROFESSIONAL,
        name: "Professional",
        tier: PlatformPlanTier.PROFESSIONAL,
        description: "For growing farms and ranches",
        pricing: {
            USD: { monthlyCents: 2900, annualMonthlyCents: 2300 }, // $29/mo or $23/mo annually (20% off)
            BRL: { monthlyCents: 14900, annualMonthlyCents: 11900 }, // R$149/mo or R$119/mo annually
        },
        // 100k tokens/mo (legacy quota) @ blended seed rate, USD. ~$1.00.
        aiBudget: { centsPerMonth: 100 },
        trialDays: 0,
        isFree: false,
        isPopular: true,
        displayFeatures: [
            "oneEstablishment",
            "upTo3Users",
            "upTo200Animals",
            "aiPoweredInsights",
            "emailSupport",
        ],
        entitlements: [
            {
                featureCode: PlatformFeatureCode.MAX_ESTABLISHMENTS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 1,
            },
            {
                featureCode: PlatformFeatureCode.MAX_STAFF_MEMBERS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 3,
            },
            {
                featureCode: PlatformFeatureCode.MAX_ANIMALS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 200,
            },
            {
                featureCode: PlatformFeatureCode.LLM_ACCESS,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.ANALYTICS_DASHBOARD,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.PRIORITY_SUPPORT,
                enabled: false,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
        ],
    },
    {
        slug: PlatformPlanSlug.ENTERPRISE,
        name: "Enterprise",
        tier: PlatformPlanTier.ENTERPRISE,
        description: "For large operations with unlimited needs",
        pricing: {
            USD: { monthlyCents: 3900, annualMonthlyCents: 3100 }, // $39/mo or $31/mo annually (20% off)
            BRL: { monthlyCents: 19900, annualMonthlyCents: 15900 }, // R$199/mo or R$159/mo annually
        },
        // 500k tokens/mo (legacy quota) @ blended seed rate, USD. ~$5.00.
        aiBudget: { centsPerMonth: 500 },
        trialDays: 0,
        isFree: false,
        isPopular: false,
        displayFeatures: [
            "upTo3Establishments",
            "upTo10Users",
            "upTo500Animals",
            "aiPoweredInsights",
            "prioritySupport",
        ],
        entitlements: [
            {
                featureCode: PlatformFeatureCode.MAX_ESTABLISHMENTS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 3,
            },
            {
                featureCode: PlatformFeatureCode.MAX_STAFF_MEMBERS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 10,
            },
            {
                featureCode: PlatformFeatureCode.MAX_ANIMALS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 500,
            },
            {
                featureCode: PlatformFeatureCode.LLM_ACCESS,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.ANALYTICS_DASHBOARD,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.PRIORITY_SUPPORT,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
        ],
    },
    {
        // Partner-sponsored plan. `isFree: true` routes enrollment through the
        // Stripe-bypass path (`subscribeToFreePlan`); `tier: STARTER` future-proofs
        // against any route later adding `requirePlan: { minTier: STARTER }` and
        // silently locking partner orgs out. See §Why item 1 of
        // docs/Plans/2026-06-27-partnership-orgs-stripe-bypass.md.
        //
        // This plan is filtered out of the user-facing plan picker by the
        // frontend (see `getStaticPlansAsDTO`) — it is only granted through the
        // partner-code redemption flow.
        slug: PlatformPlanSlug.PARTNER_BASIC,
        name: "Partner Basic",
        tier: PlatformPlanTier.STARTER,
        description: "Sponsored access through a RuralOps partnership.",
        pricing: {
            USD: { monthlyCents: 0, annualMonthlyCents: 0 },
            BRL: { monthlyCents: 0, annualMonthlyCents: 0 },
        },
        // $5 USD/month AI budget. Per-org tightening via organizationEntitlementOverrides.
        aiBudget: { centsPerMonth: 500 },
        trialDays: 0,
        isFree: true,
        isPopular: false,
        displayFeatures: [
            "upTo3Establishments",
            "upTo500Animals",
            "aiPoweredInsights",
        ],
        entitlements: [
            {
                featureCode: PlatformFeatureCode.MAX_ESTABLISHMENTS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 3,
            },
            {
                featureCode: PlatformFeatureCode.MAX_STAFF_MEMBERS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 3,
            },
            {
                featureCode: PlatformFeatureCode.MAX_ANIMALS,
                enabled: true,
                limitType: PlatformLimitType.QUANTITY,
                limitValue: 500,
            },
            {
                featureCode: PlatformFeatureCode.LLM_ACCESS,
                enabled: true,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.ANALYTICS_DASHBOARD,
                enabled: false,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
            {
                featureCode: PlatformFeatureCode.PRIORITY_SUPPORT,
                enabled: false,
                limitType: PlatformLimitType.BOOLEAN,
                limitValue: null,
            },
        ],
    },
];
/**
 * True for plans that are only granted via the partner-code redemption flow
 * and must never appear in the user-facing plan picker.
 */
export function isPartnerPlanSlug(slug) {
    return slug.startsWith("partner_");
}
// ============================================
// HELPER FUNCTIONS
// ============================================
/**
 * Get a plan by slug
 */
export function getStaticPlanBySlug(slug) {
    return STATIC_PLATFORM_PLANS.find((p) => p.slug === slug);
}
/**
 * Get the default (starter) plan
 */
export function getDefaultPlan() {
    const starter = getStaticPlanBySlug(PlatformPlanSlug.STARTER);
    if (!starter) {
        throw new Error("STATIC_PLATFORM_PLANS is missing the STARTER plan");
    }
    return starter;
}
/**
 * Get the monthly AI cost budget for a plan, in **USD cents**.
 * Falls back to a zero budget for an unknown slug (caller fails closed at
 * admission).
 */
export function getPlanAiBudget(slug) {
    const plan = getStaticPlanBySlug(slug);
    if (!plan)
        return { centsPerMonth: 0 };
    return plan.aiBudget;
}
/**
 * Convert a StaticPlatformPlan to PlatformPlanDTO (for API responses)
 */
export function staticPlanToDTO(plan, currency = "BRL") {
    return {
        id: plan.slug,
        name: plan.name,
        slug: plan.slug,
        tier: plan.tier,
        description: plan.description,
        monthlyPriceCents: plan.pricing[currency].monthlyCents,
        yearlyPriceCents: plan.pricing[currency].annualMonthlyCents * 12,
        currency,
        trialDays: plan.trialDays,
        isFree: plan.isFree,
        isPopular: plan.isPopular,
        displayFeatures: plan.displayFeatures,
        entitlements: plan.entitlements,
    };
}
/**
 * Get user-facing plans as PlatformPlanDTO array. Partner-sponsored plans
 * (`isPartnerPlanSlug` true) are excluded by default because they must only be
 * reachable through the partner-code redemption flow, never the picker.
 * Callers that need every plan (admin surfaces, seed scripts) pass
 * `{ includePartnerPlans: true }`.
 */
export function getStaticPlansAsDTO(currency = "BRL", opts = {}) {
    const source = opts.includePartnerPlans
        ? STATIC_PLATFORM_PLANS
        : STATIC_PLATFORM_PLANS.filter((p) => !isPartnerPlanSlug(p.slug));
    return source.map((p) => staticPlanToDTO(p, currency));
}
// ============================================
// PRICE-BOOK LINKAGE — how a plan here maps to a Stripe price
// ============================================
//
// This file holds plan DISPLAY + ENTITLEMENTS + AMOUNTS, but deliberately NO
// Stripe price IDs. The live Stripe price ID for every plan line is resolved at
// runtime from the STRIPE_PRICE_BOOK env var (published by the `stripe/billing`
// Terraform module), keyed by a stable `slot` that BOTH systems compute the same:
//
//     plan slot  = `${plan.slug}_${cycle}_${currency}`  lowercased
//                  e.g. "professional_monthly_brl"        (see planPriceSlot)
//     addon slot = `addon_${type}_${currency}`           (see addonPriceSlot)
//
// CONTRACT: every (plan, cycle, currency) you price below MUST have a matching
// `slot` in the catalog (`_shared/ruralops_stripe_catalog.hcl`) and therefore in
// STRIPE_PRICE_BOOK. The backend enforces this at boot
// (`assertPriceBookCoversCatalog`): a plan added here with no catalog slot fails
// startup, naming the exact missing slot string. To add or reprice a plan, edit
// the amount here AND add the slot in the catalog — see
// `_shared/ruralops_stripe_billing_STRATEGY.md`.
/**
 * Stable price-book slot for a subscription plan line:
 * `<planSlug>_<cycle>_<currency>` lowercased, e.g. `professional_monthly_brl`.
 * Must match the catalog `slot` in `ruralops_stripe_catalog.hcl`. This is the
 * sole link between a plan here and its Stripe price ID in STRIPE_PRICE_BOOK.
 */
export function planPriceSlot(slug, cycle, currency) {
    return `${slug}_${cycle.toLowerCase()}_${currency.toLowerCase()}`;
}
//# sourceMappingURL=Pricing.js.map